import { useCallback, useEffect, useRef, useState } from "react";
import Regions from "@/utils/Regions";
import FinametrixService from "@/services/FinametrixService";

export interface CascadeOption
{
    id: string;
    text: string;
}

export interface AddressCascadeState
{
    regions: CascadeOption[];
    cities: CascadeOption[];
    postalCodes: CascadeOption[];
    loadingCities: boolean;
    loadingPostalCodes: boolean;
    onRegionChange: (regionText: string) => void;
    onCityChange: (cityId: string) => void;
}

/**
 * Gestiona la carga en cascada Provincia → Ciudad → Código Postal.
 *
 * - Las regiones son estáticas (Regions.tsx), nunca se vuelven a pedir.
 * - Las ciudades se cargan UNA SOLA VEZ por provincia, guardadas en caché interna (Map).
 * - Los códigos postales se cargan UNA SOLA VEZ por ciudad, guardados en caché interna (Map).
 * - initialRegionText / initialCityId permiten restaurar el estado en modo edición sin
 *   peticiones extra: si ya hay provincia y ciudad se cargan sus listas en el montaje.
 */
export function useAddressCascade(initialRegionText?: string | null, initialCityId?: string | null): AddressCascadeState
{
    const citiesCache = useRef<Map<string, CascadeOption[]>>(new Map());
    const postalCodesCache = useRef<Map<string, CascadeOption[]>>(new Map());
    const initialCityIdRef = useRef<string | null | undefined>(initialCityId);
    const cascadeSeqRef = useRef(0);

    const regions: CascadeOption[] = Regions.map((r) => ({id: r.value, text: r.text}));

    const [cities, setCities] = useState<CascadeOption[]>([]);
    const [postalCodes, setPostalCodes] = useState<CascadeOption[]>([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [loadingPostalCodes, setLoadingPostalCodes] = useState(false);

    useEffect(() =>
    {
        initialCityIdRef.current = initialCityId;
    }, [initialCityId]);

    const sortByText = (a: CascadeOption, b: CascadeOption): number => a.text.localeCompare(b.text, "es", {sensitivity: "base"});

    const loadCities = useCallback(async (regionId: string): Promise<CascadeOption[]> =>
    {
        if(citiesCache.current.has(regionId))
        {
            return citiesCache.current.get(regionId)!;
        }
        const raw: {key: string; text: string; value: string}[] = await FinametrixService.getCitiesByRegion(regionId);
        const mapped: CascadeOption[] = raw.map((c) => ({id: c.value, text: c.text})).sort(sortByText);
        citiesCache.current.set(regionId, mapped);
        return mapped;
    }, []);

    const loadPostalCodes = useCallback(async (cityId: string): Promise<CascadeOption[]> =>
    {
        if(postalCodesCache.current.has(cityId))
        {
            return postalCodesCache.current.get(cityId)!;
        }
        const raw: {key: string; text: string; value: string}[] = await FinametrixService.getPostalCodesByCity(cityId);
        const mapped: CascadeOption[] = raw.map((p) => ({id: p.key, text: p.text})).sort(sortByText);
        postalCodesCache.current.set(cityId, mapped);
        return mapped;
    }, []);

    const resolveCityId = useCallback((loadedCities: CascadeOption[], cityValue: string): string | null =>
    {
        if(!cityValue)
        {
            return null;
        }

        const exactById = loadedCities.find((city) => city.id === cityValue);
        if(exactById)
        {
            return exactById.id;
        }

        const normalizedInput = cityValue.trim().toLowerCase();
        const matchByText = loadedCities.find((city) => city.text.trim().toLowerCase() === normalizedInput);
        if(matchByText)
        {
            return matchByText.id;
        }

        return null;
    }, []);

    const resolveRegionId = useCallback((regionValue: string): string | null =>
    {
        if(!regionValue)
        {
            return null;
        }

        const exactByText = Regions.find((region) => region.text === regionValue);
        if(exactByText)
        {
            return exactByText.value;
        }

        const exactById = Regions.find((region) => region.value === regionValue);
        if(exactById)
        {
            return exactById.value;
        }

        return null;
    }, []);

    // Restaurar estado inicial en modo edición (una sola vez al montar)
    useEffect(() =>
    {
        const seq = ++cascadeSeqRef.current;

        if(!initialRegionText)
        {
            setCities([]);
            setPostalCodes([]);
            return;
        }

        const regionId = resolveRegionId(initialRegionText);
        if(!regionId)
        {
            setCities([]);
            setPostalCodes([]);
            return;
        }

        let cancelled = false;
        setLoadingCities(true);
        setLoadingPostalCodes(false);
        setPostalCodes([]);

        loadCities(regionId).then(async (loadedCities) =>
        {
            if(cancelled || seq !== cascadeSeqRef.current)
            {
                return;
            }
            setCities(loadedCities);
            setLoadingCities(false);

            if(!initialCityIdRef.current)
            {
                return;
            }

            const resolvedCityId = resolveCityId(loadedCities, initialCityIdRef.current);
            if(!resolvedCityId)
            {
                return;
            }

            setLoadingPostalCodes(true);
            return loadPostalCodes(resolvedCityId).then((loadedPostalCodes) =>
            {
                if(cancelled || seq !== cascadeSeqRef.current)
                {
                    return;
                }
                setPostalCodes(loadedPostalCodes);
            });
        })
        .catch((err) => console.error("useAddressCascade init error:", err))
        .finally(() =>
        {
            if(cancelled || seq !== cascadeSeqRef.current)
            {
                return;
            }
            setLoadingCities(false);
            setLoadingPostalCodes(false);
        });

        return () =>
        {
            cancelled = true;
        };

    }, [initialRegionText, loadCities, loadPostalCodes, resolveCityId, resolveRegionId]);

    const onRegionChange = useCallback((regionText: string) =>
    {
        const seq = ++cascadeSeqRef.current;
        setPostalCodes([]);
        setLoadingPostalCodes(false);
        setCities([]);

        if(!regionText)
        {
            setLoadingCities(false);
            return;
        }

        const regionId = resolveRegionId(regionText);
        if(!regionId)
        {
            setLoadingCities(false);
            return;
        }

        setLoadingCities(true);
        loadCities(regionId).then((loadedCities) =>
        {
            if(seq !== cascadeSeqRef.current)
            {
                return;
            }
            setCities(loadedCities);
        })
        .catch(() =>
        {
            if(seq !== cascadeSeqRef.current)
            {
                return;
            }
            setCities([]);
        })
        .finally(() =>
        {
            if(seq !== cascadeSeqRef.current)
            {
                return;
            }
            setLoadingCities(false);
        });
    }, [loadCities, resolveRegionId]);

    const onCityChange = useCallback((cityId: string) =>
    {
        const seq = ++cascadeSeqRef.current;
        setPostalCodes([]);

        if(!cityId)
        {
            setLoadingPostalCodes(false);
            return;
        }

        setLoadingPostalCodes(true);
        loadPostalCodes(cityId).then((loadedPostalCodes) =>
        {
            if(seq !== cascadeSeqRef.current)
            {
                return;
            }
            setPostalCodes(loadedPostalCodes);
        })
        .catch(() =>
        {
            if(seq !== cascadeSeqRef.current)
            {
                return;
            }
            setPostalCodes([]);
        })
        .finally(() =>
        {
            if(seq !== cascadeSeqRef.current)
            {
                return;
            }
            setLoadingPostalCodes(false);
        });
    }, [loadPostalCodes]);

    return { regions, cities, postalCodes, loadingCities, loadingPostalCodes, onRegionChange, onCityChange };
}